import React, { useState, useCallback } from 'react'
import { Formik, Form } from 'formik'
import InputError from '../../components/InputError/InputError'
import Loading from 'custom/assets/loaders/Loading'
import NeoButton from '../../components/NeoButton/NeoButton'
import NeoInput from '../../components/NeoInput/NeoInput'
import LayoutWithOutAccountCard from '../Layout/layout-without-card'
import { LoanSchema, DepositSchema } from '../../schemas/calculator'
import NumberFormat from 'react-number-format'
import QuickLinkService from '../../services/quick-link.service'
import Modal from '../../components/Modal/Modal'
import { useModal } from 'react-modal-hook'
import { useHistory } from 'react-router-dom'
import validationConfig from 'custom/config/validation.config.json'
import { useTranslation } from 'react-i18next'
import { BUTTON_KEYS, CALCULATOR_KEYS } from '../../utils/translation-constants'
const loanConfig = validationConfig['calculator']['loan']
const depositConfig = validationConfig['calculator']['deposit']

export default function Calculator() {
  const [stage, setStage] = useState('L')
  const [isDeposit, setIsDeposit] = useState(false)
  const [result, setResult] = useState(null)
  const history = useHistory()
  const [message, setMessage] = useState('')
  const { t } = useTranslation()
  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <h6 className='text-center'>{message}</h6>
        <div className='mt-4 col-6 mx-auto'>
          <NeoButton
            type='button'
            mode='newDesign'
            text={t(BUTTON_KEYS.CLOSE)}
            onClick={() => {
              hideModal()
              history.push('/dashboard')
            }}
          />
        </div>
      </Modal>
    ),
    [message],
  )


  function renderSwitch() {
    switch (stage) {
      case 'L':
        return renderLoanCalculator()
      case 'D':
        return renderDepositCalculator()
      default:
        return renderLoanCalculator()
    }
  }

  function renderLoading() {
    return (
      <div className='mx-4 rounded d-flex justify-content-center align-items-center'>
        <Loading width='50px' height='50px' />
      </div>
    )
  }

  const calculateLoanResult = useCallback(async (data, { setSubmitting }) => {
    try {
      setSubmitting(true)
      setIsDeposit(false)
      const response = await QuickLinkService.getLoanDetails({
        accountType: 'L',
        principalAmount: data.loanAmount,
        tenor: data.loanTenure,
        interestRate: data.loanInterestRate,
      })
      setResult(response.data)
      setSubmitting(false)
    } catch (err) {
      // setMessage(err.data.message)
      // //showModal()
    }
  }, [showModal])

  const calculateDepositResult = useCallback(async (data, { setSubmitting }) => {
    try {
      setSubmitting(true)
      setIsDeposit(true)
      const response = await QuickLinkService.getLoanDetails({
        accountType: 'D',
        principalAmount: data.depositAmount,
        tenor: data.depositTenure,
        interestRate: data.depositInterestRate,
      })
      setResult(response.data)
      setSubmitting(false)
    } catch (err) {
      setSubmitting(false)
      // setMessage(err.data.message)
      // //showModal()
    }
  }, [showModal])

  function renderDepositCalculator() {
    return (
      <Formik
        validationSchema={DepositSchema}
        initialValues={{
          depositAmount: '',
          depositTenure: '',
          depositInterestRate: '',
          depositAmountFormatted: '',
        }}
        enableReinitialize
        onSubmit={calculateDepositResult}>
        {({ isSubmitting, resetForm, errors, values, touched, handleChange, setFieldValue }) => (
          <Form>
            <div className='row'>
              <div className='form-group col-12'>
                <NeoInput
                  showAsterisk={depositConfig['isDepositAmountRequired']}
                  text={t(CALCULATOR_KEYS.DEPOSIT_AMOUNT)}
                  onChange={(values) => {
                    setFieldValue('depositAmount', values.value)
                  }}
                  name='depositAmountFormatted'
                  mode='amount'
                  value={values.depositAmountFormatted}
                />
                <InputError text={errors.depositAmount} touched={touched.depositAmount} />
              </div>
            </div>
            <div className='row'>
              <div className='form-group col-12'>
              <NeoInput
                    showAsterisk={depositConfig['isTenureRequired']}
                    text={t(CALCULATOR_KEYS.TENURE)}
                    onChange={handleChange}
                    type='number'
                    mode='onlyNumber'
                    onChange={(value) => {
                      setFieldValue('depositTenure', value?.value)
                    }}
                    name='depositTenure'
                    value={values.depositTenure}
                    maxLength={3}
                  />
                  <InputError text={errors.depositTenure} touched={touched.depositTenure} />
              </div>
            </div>
            <div className='row'>
              <div className='form-group col-12'>
                <NeoInput
                  showAsterisk={depositConfig['isInterestRateRequired']}
                  text={t(CALCULATOR_KEYS.INTEREST_RATE)}
                  onChange={handleChange}
                  type='number'
                  mode='onlyNumber'
                  onChange={(value) => {
                    setFieldValue('depositInterestRate', value?.value)
                  }}
                  name='depositInterestRate'
                  value={values.depositInterestRate}
                  maxLength={3}
                />
                <InputError
                  text={errors.depositInterestRate}
                  touched={touched.depositInterestRate}
                />
              </div>
            </div>
            <div className='mt-3'>
              {result !== null ? (
                <NeoButton
                  type='button'
                  text={t(BUTTON_KEYS.CLEAR)}
                  mode='newDesign'
                  onClick={() => {
                    setResult(null)
                    resetForm()
                  }}
                />
              ) : null}
              {result === null ? (
                isSubmitting ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    type='submit'
                    mode='newDesign'
                    text={t(BUTTON_KEYS.CALCULATE)}
                    disabled={isSubmitting}
                  />
                )
              ) : null}
            </div>
          </Form>
        )}
      </Formik>
    )
  }

  function renderLoanCalculator() {
    return (
      <Formik
        initialValues={{
          loanAmount: '',
          loanTenure: '',
          loanInterestRate: '',
          loanAmountFormatted: '',
        }}
        validationSchema={LoanSchema}
        enableReinitialize
        onSubmit={calculateLoanResult}>
        {({ isSubmitting, resetForm, errors, touched, values, handleChange, setFieldValue }) => (
          <Form>
            <div className='row'>
              <div className='form-group col-12'>
                <NeoInput
                  showAsterisk={loanConfig['isLoanAmountRequired']}
                  text={t(CALCULATOR_KEYS.LOAN_AMOUNT)}
                  onChange={(values) => {
                    setFieldValue('loanAmount', values.value)
                  }}
                  mode='amount'
                  name='loanAmountFormatted'
                  value={values.loanAmountFormatted}
                />
                <InputError text={errors.loanAmount} touched={touched.loanAmount} />
              </div>
            </div>
            <div className='row'>
              <div className='form-group col-12'>
                <NeoInput
                  showAsterisk={loanConfig['isTenureRequired']}
                  text={t(CALCULATOR_KEYS.TENURE)}
                  onChange={handleChange}
                  type='number'
                  onChange={(value) => {
                    setFieldValue('loanTenure', value?.value)
                  }}
                  name='loanTenure'
                  mode='onlyNumber'
                  value={values.loanTenure}
                  maxLength={3}
                />
                <InputError text={errors.loanTenure} touched={touched.loanTenure} />
              </div>
            </div>
            <div className='row'>
              <div className='form-group col-12'>
                <NeoInput
                  showAsterisk={loanConfig['isInterestRateRequired']}
                  text={t(CALCULATOR_KEYS.INTEREST_RATE)}
                  onChange={handleChange}
                  type='number'
                  mode='onlyNumber'
                  onChange={(value) => {
                    setFieldValue('loanInterestRate', value?.value)
                  }}
                  name='loanInterestRate'
                  value={values.loanInterestRate}
                  maxLength={3}
                />
                <InputError text={errors.loanInterestRate} touched={touched.loanInterestRate} />
              </div>
            </div>
            <div className='mt-3'>
              {result !== null ? (
                <NeoButton
                  type='button'
                  text={t(BUTTON_KEYS.CLEAR)}
                  mode='newDesign'
                  onClick={() => {
                    setResult(null)
                    resetForm()
                  }}
                />
              ) : null}
              {result === null ? (
                isSubmitting ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    type='submit'
                    mode='newDesign'
                    text={t(BUTTON_KEYS.CALCULATE)}
                    disabled={isSubmitting}
                  />
                )
              ) : null}
            </div>
          </Form>
        )}
      </Formik>
    )
  }

  return (
    <LayoutWithOutAccountCard breadCumKey='CALCULATOR' showEmptySpace={true} keyPath={'CALCULATOR'}>
      <div className=''>
        <div className='row tab-navbar'>
          <div className={stage === 'L' ? 'col-4 selected' : 'col-4'}>
            <span
              onClick={() => {
                setStage('L')
                setResult(null)
              }}>
              {t(CALCULATOR_KEYS.LOANS)}
            </span>
          </div>
          <div className={stage === 'D' ? 'col-4 selected' : 'col-4'}>
            <span
              onClick={() => {
                setStage('D')
                setResult(null)
              }}>
              {t(CALCULATOR_KEYS.DEPOSITS)}
            </span>
          </div>
        </div>

        <div className='new-design-form-style'>
          {renderSwitch()}

          {result !== null && (
            <div className='my-5 py-3 px-5 rounded-1' style={{ backgroundColor: '#F15E2A' }}>
              <div className='row my-3 text-white'>
                {isDeposit == true ? (
                  <div className='col-6'>{t(CALCULATOR_KEYS.INTEREST_AMOUNT)}</div>
                ) : (
                  <div className='col-6'>{t(CALCULATOR_KEYS.MONTHLY_INSTALMENT)}</div>
                )}
                <div className='col-6 font-weight-bold text-right'>
                  <NumberFormat
                    value={Math.abs(result.emiAmount).toFixed(2)}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={''}
                  />
                </div>
              </div>
              <div className='row my-3 text-white'>
                {isDeposit == true ? (
                  <div className='col-6'>{t(CALCULATOR_KEYS.MATURITY_AMOUNT)}</div>
                ) : (
                  <div className='col-6'>{t(CALCULATOR_KEYS.TOTAL_AMOUNT)}</div>
                )}
                <div className='col-6 font-weight-bold text-right'>
                  <NumberFormat
                    value={Math.abs(result.totalAmount).toFixed(2)}
                    displayType={'text'}
                    thousandSeparator={true}
                    prefix={''}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </LayoutWithOutAccountCard>
  )
}
